import { isSemanticSearchCalledAtom } from '@components/listings-search-bar/atoms';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import {
	isAllCategoriesPageAtom,
	selectedCategoryCodesAtom,
	selectedCategoryCodesResolutionFailedAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import {
	crossCategoryMarketplaceSearchSelector,
	isCrossCategoryMarketplaceSearchActive,
} from '@redux/experiments/selectors/cross-category-marketplace-search';
import { useAppSelector } from '@redux/hooks';
import {
	buildCrossCategoryTrackingProperties,
	resolveVendorSearchSourcePage,
} from '@utils/cross-category-tracking';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import type { Raw } from 'types/vendor';
import AnalyticsConstants from '../../../../../../constants/analytics';
import Vendor from '../../../../../decorators/vendor';
import VendorAnalytics from '../../../../../decorators/vendorAnalytics';

export type TrackType =
	| 'vendor-card-click'
	| 'clear-filters-click'
	| 'close-map-view-click'
	| 'pin-clicked'
	| 'zoom-in'
	| 'zoom-out'
	| 'drag-end';

export type TrackMapView = (
	trackType: TrackType,
	vendor?: Raw | undefined,
) => void;

const SHARED_PROPS = {
	product: 'marketplace',
	sourceContent: 'map_view',
};

const buildSharedProps = (sourcePage: string) => ({
	...SHARED_PROPS,
	sourcePage,
});

const trackPinClicked = (sourcePage: string) => {
	return {
		event: AnalyticsConstants.BUTTON_CLICKED,
		properties: {
			...buildSharedProps(sourcePage),
			action: 'select_pin',
		},
	};
};

const trackVendorCardClick = (
	vendor: Raw | undefined,
	sourcePage: string,
	selectedCategories?: string[],
	isCrossCategoryActive?: boolean,
	experimentVariant?: string | null,
	selectedCategoriesResolutionFailed?: boolean,
) => {
	if (!vendor) {
		return {
			event: '',
			properties: {},
		};
	}

	const decoratedVendor = new Vendor(vendor);
	const vendorAnalytics = new VendorAnalytics(decoratedVendor);
	return {
		event: AnalyticsConstants.CLICK_THROUGH_TO_VENDORS,
		properties: {
			...buildSharedProps(sourcePage),
			...vendorAnalytics.baseEventProps(),
			...buildCrossCategoryTrackingProperties({
				isCrossCategoryActive,
				experimentVariant,
				selectedCategories,
				selectedCategoriesResolutionFailed,
			}),
		},
	};
};

const trackClearFiltersClick = (sourcePage: string) => {
	return {
		event: AnalyticsConstants.BUTTON_CLICKED,
		properties: {
			...buildSharedProps(sourcePage),
			action: 'clear_filters',
		},
	};
};

const trackCloseMapViewClick = (sourcePage: string) => {
	return {
		event: AnalyticsConstants.BUTTON_CLICKED,
		properties: {
			...buildSharedProps(sourcePage),
			action: 'close_map',
		},
	};
};

const trackMapScroll = (sourcePage: string) => {
	return {
		event: AnalyticsConstants.BUTTON_CLICKED,
		properties: {
			...buildSharedProps(sourcePage),
			action: 'scroll',
		},
	};
};

const trackMapZoomIn = (sourcePage: string) => {
	return {
		event: AnalyticsConstants.BUTTON_CLICKED,
		properties: {
			...buildSharedProps(sourcePage),
			action: 'zoom_in',
		},
	};
};

const trackMapZoomOut = (sourcePage: string) => {
	return {
		event: AnalyticsConstants.BUTTON_CLICKED,
		properties: {
			...buildSharedProps(sourcePage),
			action: 'zoom_out',
		},
	};
};

export const useTrackMapView = () => {
	const { track } = useAnalyticsContext();
	const selectedCategoryCodes = useAtomValue(selectedCategoryCodesAtom);
	const selectedCategoriesResolutionFailed = useAtomValue(
		selectedCategoryCodesResolutionFailedAtom,
	);
	const isAllCategoriesPage = useAtomValue(isAllCategoriesPageAtom);
	const isSemanticSearchCalled = useAtomValue(isSemanticSearchCalledAtom);
	const isCrossCategoryActive = useAppSelector(
		isCrossCategoryMarketplaceSearchActive,
	);
	const experimentVariant = useAppSelector(
		crossCategoryMarketplaceSearchSelector,
	);

	const sourcePage = resolveVendorSearchSourcePage({
		isSemanticSearch: isSemanticSearchCalled,
		isAllCategoriesPage,
	});

	return useCallback(
		(trackType: TrackType, vendor?: Raw) => {
			const resolvedSourcePage = sourcePage;
			const trackFunctions = {
				'pin-clicked': () => trackPinClicked(resolvedSourcePage),
				'vendor-card-click': () =>
					trackVendorCardClick(
						vendor,
						resolvedSourcePage,
						selectedCategoryCodes,
						isCrossCategoryActive,
						experimentVariant,
						selectedCategoriesResolutionFailed,
					),
				'clear-filters-click': () => trackClearFiltersClick(resolvedSourcePage),
				'close-map-view-click': () =>
					trackCloseMapViewClick(resolvedSourcePage),
				'drag-end': () => trackMapScroll(resolvedSourcePage),
				'zoom-in': () => trackMapZoomIn(resolvedSourcePage),
				'zoom-out': () => trackMapZoomOut(resolvedSourcePage),
			};

			if (trackFunctions[trackType]) {
				return track({
					...trackFunctions[trackType](),
				});
			}
		},
		[
			track,
			selectedCategoryCodes,
			selectedCategoriesResolutionFailed,
			sourcePage,
			isCrossCategoryActive,
			experimentVariant,
		],
	);
};
